import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';
import { OrdersService } from '../../core/services/orders.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';

interface Supplier { id: string; name: string; }

@Component({
  selector: 'app-purchase-list',
  imports: [FormsModule, DatePipe, RouterLink],
  templateUrl: './purchase-list.html',
  styleUrl: './purchase-list.scss',
})
export class PurchaseList implements OnInit {
  private readonly orders = inject(OrdersService);
  private readonly cdr = inject(ChangeDetectorRef);
  purchases: any[] = [];
  suppliers: Supplier[] = [];
  supplierFilter = '';
  fromDate = '';
  toDate = '';
  loading = true;
  voucherLoadingId = '';
  errorMessage = '';
  successMessage = '';
  selectedPurchase: any | null = null;

  ngOnInit(): void { this.load(); }

  get filteredPurchases(): any[] {
    const from = this.fromDate ? new Date(`${this.fromDate}T00:00:00`).getTime() : null;
    const to = this.toDate ? new Date(`${this.toDate}T23:59:59.999`).getTime() : null;
    return this.purchases.filter((purchase) => {
      const purchasedAt = new Date(purchase.purchasedAt ?? purchase.createdAt).getTime();
      return (!this.supplierFilter || purchase.supplierId === this.supplierFilter || purchase.supplier?.id === this.supplierFilter) &&
        (from === null || purchasedAt >= from) && (to === null || purchasedAt <= to);
    });
  }

  load(): void {
    this.loading = true;
    this.errorMessage = '';
    forkJoin({ purchases: this.orders.purchases(), suppliers: this.orders.suppliers() })
      .pipe(finalize(() => { this.loading = false; this.cdr.detectChanges(); }))
      .subscribe({
        next: ({ purchases, suppliers }) => {
          this.purchases = purchases;
          const allSuppliers = new Map<string, Supplier>();
          suppliers.forEach((supplier) => allSuppliers.set(supplier.id, supplier));
          purchases.forEach((purchase) => {
            const supplier = purchase.supplier;
            if (supplier?.id && supplier?.name) allSuppliers.set(supplier.id, { id: supplier.id, name: supplier.name });
          });
          this.suppliers = [...allSuppliers.values()].sort((left, right) => left.name.localeCompare(right.name));
        },
        error: (error) => this.errorMessage = httpErrorMessage(error, 'Unable to load purchases.'),
      });
  }

  clearFilters(): void { this.supplierFilter = ''; this.fromDate = ''; this.toDate = ''; }
  supplierName(purchase: any): string {
    return purchase.supplier?.name ?? this.suppliers.find((supplier) => supplier.id === purchase.supplierId)?.name ?? 'Supplier';
  }
  money(value: number): string { return `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`; }
  viewPurchase(purchase: any): void { this.selectedPurchase = purchase; }
  closePurchase(): void { this.selectedPurchase = null; }
  evidence(value: unknown, fallback?: string | null): string[] {
    let urls: string[] = [];
    if (Array.isArray(value)) urls = value.filter((item): item is string => typeof item === 'string' && !!item);
    else if (typeof value === 'string' && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) urls = parsed.filter((item): item is string => typeof item === 'string' && !!item);
      } catch { urls = []; }
    }
    if (!urls.length && fallback) urls = [fallback];
    return [...new Set(urls)];
  }
  isPdf(url: string): boolean { return url.split('?')[0].toLowerCase().endsWith('.pdf'); }

  openOrGenerateVoucher(purchase: any): void {
    if (purchase.voucherUrl) { window.open(purchase.voucherUrl, '_blank', 'noopener'); return; }
    this.voucherLoadingId = purchase.id;
    this.errorMessage = '';
    this.orders.generatePurchaseVoucher(purchase.id).pipe(finalize(() => {
      this.voucherLoadingId = '';
      this.cdr.detectChanges();
    })).subscribe({
      next: (result) => {
        const url = (result?.voucher ?? result)?.url ?? '';
        this.purchases = this.purchases.map((item) => item.id === purchase.id ? { ...item, voucherUrl: url } : item);
        this.successMessage = 'Purchase voucher generated successfully.';
        if (url) window.open(url, '_blank', 'noopener');
      },
      error: (error) => this.errorMessage = httpErrorMessage(error, 'Voucher generation is still unavailable. Please try again.'),
    });
  }
}
